import { FC, useState } from "react";
import useStore from "../../../../../../helpers/useStore";
import { useMasterTables } from "../../../../../../stores/MasterTables";
import BillBoxWrapper from "../../../DirectBilling/common/components/BillBoxWrapper";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import { Grid } from "@mui/material";
import PaperNewBillInfo from "../components/PaperNewBillInfo";
import TransactionRxOverview from "../../../Base/components/TransactionRxOverview";
import { ExportBillingData } from "../../../../../../api-client";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import { BillingTypeEnum, ScreenMode } from "../../../Base/types/insuranceBills.types";
import PaperEditFullScreen from "../../Edit/pages/PaperEditFullScreen";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";

interface PaperFullScreenBillProps {
  handleFullScreenDialogClose: () => void;
  selectedAccount: string;
  userName: string;
  userId: string;
}

const PaperFullScreenBill: FC<PaperFullScreenBillProps> = ({
  handleFullScreenDialogClose,
  selectedAccount,
  userName,
  userId,
}) => {
  /**
   * Setting a mode to determine if we are in "new" or "edit" mode and a state to hold the bills that are being edited.
   * If we are in "new" mode, we will show the PaperNewBillInfo component.
   * If we are in "edit" mode, we will show the PaperEditFullScreen component.
   * When the user saves the bills, we will switch back to "edit" mode and update the editBills state with the newly created bills.
   * This allows us to edit the bills that were just created.
   * If the user closes the editor, we will switch back to "new" mode and clear the editBills state.
   */
  const [mode, setMode] = useState<ScreenMode>(ScreenMode.NEW);
  const [editBills, setEditBills] = useState<ExportBillingData[]>([]);

  // Other states
  const [selectedRx, setSelectedRx] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const { data: masterTablesData } = useMasterTables();
  const [editRxMode, setEditRxMode] = useState<boolean>(false);
  const transactionStore = useStore(TransactionsContext);
  const { getBillingDataForEditing } = transactionStore;

  /**
   * This function is called when the user saves the bills.
   * It fetches the billing data for editing and sets the editBills state with the fetched data.
   * It also switches the mode to "edit" to show the PaperEditFullScreen component.
   */
  const handleBillsSaved = async (ids: string[]) => {
    try {
      setIsLoadingEdit(true);
      const bills = await getBillingDataForEditing(ids, BillingTypeEnum.PAPER);
      setEditBills(bills);
      setMode(ScreenMode.EDIT);
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const handleRxDelete = (deletedRxId: string) => {
    if (selectedRx && selectedRx.id === deletedRxId) {
      setSelectedRx(null);
      setIsVisible(false);
    }
  };

  const handleRowClick = (rx: any) => {
    setSelectedRx(rx);
    setIsVisible(true);
  };

  const handleValidationFilter = (remainingRxs: any[]) => {
    // Check if the selected RX is no longer in the list of remaining RXs
    if (selectedRx && !remainingRxs.some((rx) => rx.id === selectedRx.id)) {
      setSelectedRx(null);
      setIsVisible(false);
    }
  };

  const handleRemoveAll = (removedBill: any) => {
    // If the currently selected Rx is from the bill being removed, un-select it
    if (selectedRx && removedBill.rxs.some((rx: any) => rx.id === selectedRx.id)) {
      setSelectedRx(null);
      setIsVisible(false);
    }
  };

  /* ---------  render  --------- */

  /**
   * If we are in "edit" mode, we show the PaperEditFullScreen component.
   * This component will handle the editing of the bills and will call the handleFullScreenDialogClose function
   * when the user closes the editor.
   * When the editor is closed, we reset the mode to "new" and clear the editBills state.
   * This allows us to return to the "new" mode and start creating new bills again.
   */
  if (mode === ScreenMode.EDIT) {
    return (
      <PaperEditFullScreen
        handleFullScreenDialogClose={() => {
          setMode(ScreenMode.NEW);
          setEditBills([]);
          handleFullScreenDialogClose?.();
        }}
        initialBills={editBills}
      />
    );
  }

  return (
    <>
    <WaitingBackdrop isWorking={isLoadingEdit} message="Bitte warten" />
    <Grid
      container
      spacing={1}
      sx={{ height: "100%", marginTop: "1px" }}
    >
      <BillBoxWrapper xs={5}>
        <TopLevelPaper
          className="scrollable rounded-container"
          sx={{ paddingTop: "0px" }}
        >
          {/* Pass handleRowClick to BillInfo */}
          <PaperNewBillInfo
            handleFullScreenDialogClose={handleFullScreenDialogClose}
            onRowClick={handleRowClick}
            account={selectedAccount}
            userName={userName}
            userId={userId}
            onRxDelete={handleRxDelete}
            onValidationFilter={handleValidationFilter}
            onBillsSaved={handleBillsSaved}
            onRemoveAll={handleRemoveAll}
          />
        </TopLevelPaper>
      </BillBoxWrapper>
      {isVisible && (
        <>
          <BillBoxWrapper xs={7}>
            <TopLevelPaper className="scrollable rounded-container">
              {/* Pass the selected rx to BillOverview */}
              <TransactionRxOverview
                rowData={selectedRx}
                masterData={masterTablesData}
                editRxMode={editRxMode}
                setEditRxMode={setEditRxMode}
              />
            </TopLevelPaper>
          </BillBoxWrapper>
        </>
      )}
    </Grid>
    </>
  );
};

export default PaperFullScreenBill;
